import React, { useState, useMemo } from 'react';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';
import { SonoReport } from '../../types/sonoReport';

interface Props {
  reports: SonoReport[];
}

type View = 'global' | 'detail';
type Period = '1m' | '3m' | '6m' | '1y' | 'all';

const PERIOD_LABELS: Record<Period, string> = {
  '1m': '1 mois', '3m': '3 mois', '6m': '6 mois', '1y': '1 an', 'all': 'Tout'
};

function computeScores(r: SonoReport) {
  const bs = r.beforeService;
  const ds = r.duringService;
  const as_ = r.afterService;

  const okV = (v: string) => v === 'OK' ? 1 : 0;
  const effectV = (v: string) => v === 'EFFECTUEE' ? 1 : 0;
  const satV = (v: string) => v === 'SATISFAISANT' ? 1 : 0;
  const qualV = (v: string) => v === 'BONNE' ? 1 : v === 'MOYENNE' ? 0.5 : 0;
  const ouiV = (v: string) => v === 'OUI' ? 1 : 0;
  const vidV = (v: string) => v === 'OK' ? 1 : v === 'EN_COURS' ? 0.5 : 0;

  const avant = [
    okV(bs.materialCheck), okV(bs.soundQualityTest),
    okV(bs.liveStreamingTest), okV(bs.onlineSoundTest),
    okV(bs.onlineVideoTest), okV(bs.photoEquipmentPrep),
  ];
  const pendant = [
    okV(ds.proclamationLaunch), satV(ds.roomSoundQuality),
    ouiV(ds.liveStreaming), qualV(ds.onlineSoundQuality),
    qualV(ds.onlineVideoQuality), ouiV(ds.photoshootDuringService),
  ];
  const apres = [
    effectV(as_.servantsPhotos), effectV(as_.photoMasking),
    okV(as_.audioReplayPublication), vidV(as_.videoReplayPublication),
    okV(as_.fileArchiving),
  ];

  const scoreAvant = Math.round((avant.reduce((s, v) => s + v, 0) / avant.length) * 100);
  const scorePendant = Math.round((pendant.reduce((s, v) => s + v, 0) / pendant.length) * 100);
  const scoreApres = Math.round((apres.reduce((s, v) => s + v, 0) / apres.length) * 100);
  const scoreGlobal = Math.round((scoreAvant + scorePendant + scoreApres) / 3);
  return { scoreAvant, scorePendant, scoreApres, scoreGlobal };
}

function filterByPeriod(reports: SonoReport[], period: Period, from: string, to: string) {
  const now = new Date();
  let start: Date | null = null;
  if (period === '1m') start = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
  else if (period === '3m') start = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
  else if (period === '6m') start = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
  else if (period === '1y') start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
  return reports.filter(r => {
    const d = new Date(r.date);
    if (from) return d >= new Date(from) && (!to || d <= new Date(to));
    if (start) return d >= start!;
    return true;
  }).sort((a, b) => a.date.localeCompare(b.date));
}

export default function SonoTrendChart({ reports }: Props) {
  const [view, setView] = useState<View>('global');
  const [period, setPeriod] = useState<Period>('3m');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const filtered = useMemo(() => filterByPeriod(reports, period, from, to), [reports, period, from, to]);

  const chartData = useMemo(() => filtered.map(r => {
    const s = computeScores(r);
    return {
      date: new Date(r.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
      ...s,
    };
  }), [filtered]);

  const avg = (key: 'scoreGlobal' | 'scoreAvant' | 'scorePendant' | 'scoreApres') =>
    chartData.length > 0 ? Math.round(chartData.reduce((s, d) => s + d[key], 0) / chartData.length) : 0;

  const scoreColor = (s: number) => s >= 80 ? 'text-green-700' : s >= 60 ? 'text-amber-700' : 'text-red-700';

  if (reports.length === 0) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Évolution Sono & Communication</h3>

      {/* Résumé */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-indigo-50 rounded-lg p-3 text-center">
          <div className={`text-2xl font-bold ${scoreColor(avg('scoreGlobal'))}`}>{avg('scoreGlobal')}%</div>
          <div className="text-xs text-indigo-600">Score global moy.</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <div className={`text-2xl font-bold ${scoreColor(avg('scoreAvant'))}`}>{avg('scoreAvant')}%</div>
          <div className="text-xs text-blue-600">Score avant</div>
        </div>
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <div className={`text-2xl font-bold ${scoreColor(avg('scorePendant'))}`}>{avg('scorePendant')}%</div>
          <div className="text-xs text-green-600">Score pendant</div>
        </div>
        <div className="bg-amber-50 rounded-lg p-3 text-center">
          <div className={`text-2xl font-bold ${scoreColor(avg('scoreApres'))}`}>{avg('scoreApres')}%</div>
          <div className="text-xs text-amber-600">Score après</div>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex gap-2 mb-4">
        {([['global', 'Score global'], ['detail', 'Détail']] as [View, string][]).map(([v, l]) => (
          <button key={v} onClick={() => setView(v)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${view === v ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {l}
          </button>
        ))}
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-2 items-end mb-4">
        <div className="flex gap-2 flex-wrap">
          {(Object.entries(PERIOD_LABELS) as [Period, string][]).map(([p, l]) => (
            <button key={p} onClick={() => { setPeriod(p); setFrom(''); setTo(''); }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${period === p && !from ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-end ml-auto">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Du</label>
            <input type="date" value={from} onChange={e => { setFrom(e.target.value); setPeriod('all'); }}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Au</label>
            <input type="date" value={to} onChange={e => { setTo(e.target.value); setPeriod('all'); }}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
        </div>
      </div>

      {chartData.length === 0 ? (
        <div className="h-48 flex items-center justify-center text-gray-400">Aucune donnée pour cette période</div>
      ) : view === 'global' ? (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v: number) => `${v}%`} />
            <Legend />
            <Line type="monotone" dataKey="scoreGlobal" name="Score global" stroke="#4f46e5" strokeWidth={2} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v: number) => `${v}%`} />
            <Legend />
            <Bar dataKey="scoreAvant" name="Avant" fill="#3b82f6" />
            <Bar dataKey="scorePendant" name="Pendant" fill="#22c55e" />
            <Bar dataKey="scoreApres" name="Après" fill="#f59e0b" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}